import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { evaluationsAPI } from '../lib/api';
import { Part2Panel } from '../components/Part2Panel';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Save, Send, PenLine, Printer, CheckCircle, AlertCircle, Mail, RefreshCw, Trash2, Undo2, MessageSquarePlus } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { SupervisorLogPanel } from '../components/SupervisorLogPanel';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import RatingScalePanel from '../components/RatingScalePanel';
import CredentialsPanel from '../components/CredentialsPanel';
import { formatDate, cn } from '../lib/utils';
import { stateLabel } from '../lib/usStates';

// Strip common Markdown syntax from text pasted into narrative fields. The
// old form left the raw asterisks in place, which printed literally on the
// PDF. This handler intercepts paste events, cleans the payload, and inserts
// plain text at the cursor. Backend also sanitizes as a safety net.
const stripMarkdown = (raw) => {
  if (!raw) return raw;
  return raw
    .replace(/\*\*(.+?)\*\*|__(.+?)__/g, (_, a, b) => a || b)   // **bold** / __bold__
    .replace(/(?<![*_])[*_]([^*_\n]+?)[*_](?![*_])/g, '$1')      // *italic* / _italic_
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')                          // # headings
    .replace(/^\s*[*\-+]\s+/gm, '');                             // - bullets
};

const handlePlainPaste = (setter) => (e) => {
  const raw = e.clipboardData?.getData('text');
  if (raw == null) return;
  e.preventDefault();
  const cleaned = stripMarkdown(raw);
  const t = e.target;
  const start = t.selectionStart ?? t.value.length;
  const end = t.selectionEnd ?? t.value.length;
  const next = t.value.slice(0, start) + cleaned + t.value.slice(end);
  setter(next);
  // Restore caret after React re-renders
  requestAnimationFrame(() => {
    try {
      t.selectionStart = t.selectionEnd = start + cleaned.length;
    } catch (_e) { /* noop */ }
  });
};

const STATUS_LABELS = {
  draft: 'Draft',
  awaiting_employee: 'Awaiting Employee',
  awaiting_supervisor: 'Awaiting Supervisor',
  awaiting_reviewer: 'Awaiting Reviewer/Manager',
  complete: 'Complete',
  declined: 'Declined',
  // legacy
  in_review: 'Awaiting Employee',
  pending_reviewer: 'Awaiting Reviewer/Manager',
  pending_hr: 'Awaiting Reviewer/Manager',
};

const STAGE_OPTIONS = [
  { value: 'supervisor', label: 'Direct Supervisor', statuses: ['awaiting_supervisor'] },
  { value: 'reviewer', label: 'Reviewer / Manager', statuses: ['awaiting_reviewer'] },
];

export default function EvaluationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const perms = useEffectivePermissions();
  // Capability, not role list: `supervisor` and the GSOC supervisory roles hold
  // evaluations edit and were previously locked out of every action button on
  // this page (2026-09-08). Span of control is enforced server-side.
  // `add` is deliberately NOT accepted here: the buttons on this page are
  // update / submit / regenerate-link, all of which the API gates on `edit`,
  // so an add-only role would see controls that 403 (code review 2026-09-09).
  const canWriteEvals = perms.can('evaluations', 'edit') || hasRole('admin', 'hr', 'manager');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ev, setEv] = useState(null);
  const [template, setTemplate] = useState(null);
  const [liveSnap, setLiveSnap] = useState(null);  // CO4: live employee snapshot
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [signRole, setSignRole] = useState(null);
  // v4.33.4: supervisor signs AND submits in one step from the bottom of the form.
  const [combinedSubmit, setCombinedSubmit] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineNote, setDeclineNote] = useState('');
  // New signing order (2026-06): reviewer return + optional post-sign response.
  const [showReturn, setShowReturn] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnBusy, setReturnBusy] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [responseRole, setResponseRole] = useState('supervisor');
  const [responseText, setResponseText] = useState('');
  const [responseBusy, setResponseBusy] = useState(false);

  // Form draft state (mirrored from ev for editing)
  const [ratings, setRatings] = useState({});  // item_id -> {rating, na, comments, core_value_example}
  const [strengths, setStrengths] = useState('');
  const [areas, setAreas] = useState('');
  const [goals, setGoals] = useState('');
  const [training, setTraining] = useState('');
  const [supComments, setSupComments] = useState('');
  const [empComments, setEmpComments] = useState('');
  const [armedLicNum, setArmedLicNum] = useState('');
  const [armedLicExp, setArmedLicExp] = useState('');
  // Licensed-state snapshot from the employee's primary license (set at
  // evaluation creation; read-only in the eval form).
  const [licensedState, setLicensedState] = useState('');

  // 2026-08-23: 90-Day Trial Recommendation block — a single free-text
  // Justification carries the "why" for every choice. Rendered only when
  // ev.evaluation_type === '90-Day Trial'; other types leave these empty.
  const [recommendation, setRecommendation] = useState('');
  const [recommendationBasis, setRecommendationBasis] = useState('');

  // Signature state
  const [empSig, setEmpSig] = useState('');
  const [supSig, setSupSig] = useState('');
  const [revSig, setRevSig] = useState('');
  const [hrSig, setHrSig] = useState('');
  // Per-signature comments (captured by the supervisor / reviewer at signing time and
  // persisted on the signatures.{role}.comments field; shown on the PDF and read-only views).
  const [supSigComments, setSupSigComments] = useState('');
  const [revSigComments, setRevSigComments] = useState('');
  // "Who can sign this?" panel (v4.37.0) — visible to supervisory team, HR, admins.
  const [whoCanSign, setWhoCanSign] = useState(null);

  useEffect(() => { loadAll(); }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    evaluationsAPI.whoCanSign(id)
      .then((r) => { if (active) setWhoCanSign(r.data); })
      .catch(() => { if (active) setWhoCanSign(null); });  // 403 => not permitted, hide panel
    return () => { active = false; };
  }, [id]);

  // CO4: refresh the live employee snapshot whenever the window regains focus
  // so manager edits to the employee record are picked up without reload.
  useEffect(() => {
    const onFocus = () => { if (id) refreshLiveSnap(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [id]);

  const refreshLiveSnap = async () => {
    try {
      const res = await evaluationsAPI.liveEmployeeSnapshot(id);
      setLiveSnap(res.data);
    } catch { /* non-fatal */ }
  };

  const loadAll = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await evaluationsAPI.get(id);
      setEv(res.data);
      hydrateDraft(res.data);
      // 2026-08-22: pass evaluation_type + evaluation_track so the 90-Day
      // template is served for 90-Day Trials (Annual keeps post_type filtering).
      const tplRes = await evaluationsAPI.template(
        res.data.employee_snapshot?.post_type,
        res.data.evaluation_type || 'Annual',
        res.data.evaluation_track || 'security_officer',
      );
      setTemplate(tplRes.data);
      // CO4: load live employee snapshot in parallel
      try {
        const liveRes = await evaluationsAPI.liveEmployeeSnapshot(id);
        setLiveSnap(liveRes.data);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error('Failed to load evaluation');
      navigate('/evaluations');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const hydrateDraft = (data) => {
    const r = {};
    (data.ratings || []).forEach(item => { r[item.item_id] = { ...item }; });
    setRatings(r);
    setStrengths(data.strengths || '');
    setAreas(data.areas_for_development || '');
    setGoals(data.goals || '');
    setTraining(data.training_assigned || '');
    setSupComments(data.supervisor_comments || '');
    setEmpComments(data.employee_comments || '');
    setArmedLicNum(data.armed_license_number || '');
    setArmedLicExp(data.armed_license_expiration || '');
    setLicensedState(data.licensed_state || '');
    setRecommendation(data.recommendation || '');
    setRecommendationBasis(data.recommendation_basis || '');
  };

  const isDraft = ev?.status === 'draft';
  const isComplete = ev?.status === 'complete';
  // CO4: while in Draft, display the LIVE employee data; after submission the frozen snapshot is authoritative.
  const snap = (isDraft && liveSnap) ? liveSnap : (ev?.employee_snapshot || {});

  // Compute live section averages and overall
  const { sectionAvgs, overall } = useMemo(() => {
    if (!template) return { sectionAvgs: {}, overall: null };
    const bySec = {};
    template.sections.forEach(sec => {
      const vals = sec.items.map(it => ratings[it.id]).filter(r => r && !r.na && r.rating).map(r => r.rating);
      if (vals.length) bySec[sec.id] = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
    });
    const allVals = Object.values(bySec);
    const ov = allVals.length ? +(allVals.reduce((a, b) => a + b, 0) / allVals.length).toFixed(2) : null;
    return { sectionAvgs: bySec, overall: ov };
  }, [ratings, template]);

  const overallBand = useMemo(() => {
    if (overall == null || !template) return null;
    for (const b of template.bands) {
      if (overall >= b.min && overall < b.max_excl) return b.label;
    }
    return null;
  }, [overall, template]);

  const updateRating = (itemId, patch) => {
    setRatings(prev => ({ ...prev, [itemId]: { item_id: itemId, ...(prev[itemId] || {}), ...patch } }));
  };

  const handleSave = async () => {
    if (!isDraft) return;
    // 2026-08: comment-required rule — block save when a rating in
    // template.comment_required_ratings has no accompanying comment.
    const required = new Set(template?.comment_required_ratings || [1, 2]);
    const missing = Object.values(ratings).filter(r =>
      r && !r.na && required.has(r.rating) && !(r.comments || '').trim()
    );
    if (missing.length) {
      toast.error(`Comments required for items rated ${[...required].sort().join(', ')}. Missing on: ${missing.map(m => m.item_id).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const is90Day = (ev?.evaluation_type || 'Annual') === '90-Day Trial';
      const payload = {
        ratings: Object.values(ratings),
        supervisor_comments: supComments,
        employee_comments: empComments,
        // armed_license_number / armed_license_expiration / licensed_state are
        // snapshots taken at evaluation creation from the employee's primary
        // license. They are read-only here — edits must happen in the
        // Licensing section of the employee record.
      };
      // 2026-08-23: Strengths / Areas / Goals / Training are Annual-only.
      // On 90-Day evals the Recommendation Justification carries the write-up,
      // so we omit these fields entirely (existing values on the record are
      // preserved server-side because we don't $unset them).
      if (!is90Day) {
        payload.strengths = strengths;
        payload.areas_for_development = areas;
        payload.goals = goals;
        payload.training_assigned = training;
      }
      // Include the 90-Day Trial Recommendation block only when the evaluation
      // is of that type. The 2026-08-23 build retired extension_days and
      // reevaluation_date — a single Justification (recommendation_basis)
      // now carries the "why" for every recommendation.
      if (is90Day) {
        payload.recommendation = recommendation || null;
        payload.recommendation_basis = recommendationBasis || null;
      }
      const res = await evaluationsAPI.update(id, payload);
      setEv(res.data);
      toast.success('Draft saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Validation: every rating row in every section must have a numeric rating (1-5) OR be marked N/A.
  // Returns { ok, sectionsWithMisses: [{section, missing:[itemLabel,...]}] }
  const submissionReadiness = useMemo(() => {
    if (!template) return { ok: false, sectionsWithMisses: [] };
    const sectionsWithMisses = [];
    for (const sec of (template.sections || [])) {
      const missing = [];
      for (const item of (sec.items || [])) {
        const r = ratings[item.id] || {};
        const rated = (typeof r.rating === 'number' && r.rating >= 1 && r.rating <= 5);
        if (!rated && !r.na) missing.push(item.label || item.id);
      }
      if (missing.length) sectionsWithMisses.push({ section: sec.label || sec.id || sec.section, missing });
    }
    return { ok: sectionsWithMisses.length === 0, sectionsWithMisses };
  }, [template, ratings]);

  const handleSubmit = async () => {
    if (!isDraft) return;
    // CO4: refresh the live snapshot RIGHT NOW so any just-applied email/supervisor edit is reflected.
    let currentSnap = liveSnap;
    try {
      const live = await evaluationsAPI.liveEmployeeSnapshot(id);
      currentSnap = live.data;
      setLiveSnap(currentSnap);
    } catch { /* fall back to whatever we have */ }
    const email = (currentSnap?.email || '').trim();
    if (!email) {
      toast.error('Employee has no email on file. Add an email on the employee record before submitting.');
      return;
    }
    if (!submissionReadiness.ok) {
      const first = submissionReadiness.sectionsWithMisses[0];
      toast.error(`Every item must be rated or marked N/A. Start with ${first.section}: ${first.missing.length} item(s) unrated.`);
      // Scroll to first unrated section
      const target = document.querySelector('[data-eval-incomplete="true"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // 2026-08-23: 90-Day evals need a recommendation + Justification before
    // routing to the employee. Backend enforces the same gate; the client-side
    // check keeps the error inline rather than surfacing as an API failure.
    if ((ev?.evaluation_type || 'Annual') === '90-Day Trial') {
      if (!['Retain', 'Extend', 'Separate'].includes(recommendation)) {
        toast.error('Pick a 90-Day Trial recommendation (Retain, Extend, or Separate) before submitting.');
        document.querySelector('[data-testid="trial-recommendation-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (!recommendationBasis.trim()) {
        toast.error('Justification is required for every 90-Day Trial recommendation.');
        document.querySelector('[data-testid="rec-basis"]')?.focus();
        return;
      }
    }
    const newOrder = (ev?.signing_order || 'old') === 'new';
    if (newOrder) {
      if (!window.confirm('This locks the evaluation for signing and routes it to the supervisor to sign first. The reviewer signs next, and the employee signs last through a secure link. Continue?')) return;
    } else if (!window.confirm(`This will email a 7-day secure signing link to ${email}. Continue?`)) {
      return;
    }
    await handleSave();
    try {
      await evaluationsAPI.submit(id);
      toast.success(newOrder ? 'Submitted for signing — ready for the supervisor to sign.' : 'Submitted. Signing link emailed to employee.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
  };

  const openSignDialog = (role) => {
    setSignRole(role);
    setSupSig(''); setRevSig('');
    setSupSigComments(''); setRevSigComments('');
    setShowSign(true);
  };

  const handleSign = async () => {
    try {
      let payload;
      if (signRole === 'supervisor') {
        if (!supSig) { toast.error('Signature required'); return; }
        payload = { role: 'supervisor', signature: supSig, signature_comments: supSigComments };
      } else if (signRole === 'reviewer') {
        if (!revSig) { toast.error('Signature required'); return; }
        payload = { role: 'reviewer', signature: revSig, signature_comments: revSigComments };
      } else {
        toast.error('Unsupported signing role'); return;
      }
      const res = await evaluationsAPI.sign(id, payload);
      setEv(res.data);
      setShowSign(false);
      toast.success('Signed');
      if (res.data.status === 'complete') toast.info('Evaluation complete — PDF will be filed to employee record.');
    } catch (e) {
      // 409 means "nothing to do here" — already signed, already complete, or
      // waiting on somebody else. That is information, not a failure, so it
      // reads as a notice and the record is refreshed so the screen agrees.
      if (e.response?.status === 409) {
        setShowSign(false);
        toast.info(e.response.data?.detail || 'This evaluation has already been signed.',
                   { duration: 8000 });
        loadAll();
        return;
      }
      toast.error(e.response?.data?.detail || 'Sign failed');
    }
  };

  // v4.33.4 Item 1 — validate completeness, then open the sign dialog so the
  // supervisor comments + signs and submits to the reviewer in one action.
  const startSupervisorSignSubmit = async () => {
    if (!isDraft) return;
    let currentSnap = liveSnap;
    try { const live = await evaluationsAPI.liveEmployeeSnapshot(id); currentSnap = live.data; setLiveSnap(currentSnap); } catch { /* use what we have */ }
    if (!((currentSnap?.email || '').trim())) {
      toast.error('Employee has no email on file. Add an email on the employee record before submitting.');
      return;
    }
    if (!submissionReadiness.ok) {
      const first = submissionReadiness.sectionsWithMisses[0];
      toast.error(`Every item must be rated or marked N/A. Start with ${first.section}: ${first.missing.length} item(s) unrated.`);
      document.querySelector('[data-eval-incomplete="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if ((ev?.evaluation_type || 'Annual') === '90-Day Trial') {
      if (!['Retain', 'Extend', 'Separate'].includes(recommendation)) {
        toast.error('Pick a 90-Day Trial recommendation (Retain, Extend, or Separate) before submitting.');
        document.querySelector('[data-testid="trial-recommendation-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (!recommendationBasis.trim()) {
        toast.error('Justification is required for every 90-Day Trial recommendation.');
        document.querySelector('[data-testid="rec-basis"]')?.focus();
        return;
      }
    }
    setSignRole('supervisor'); setSupSig(''); setSupSigComments('');
    setCombinedSubmit(true); setShowSign(true);
  };

  const handleSignAndSubmit = async () => {
    if (!supSig) { toast.error('Signature required'); return; }
    try {
      await handleSave();
      await evaluationsAPI.submit(id);
      // Supervisor signature moves it to the reviewer (or the next step when there
      // is no reviewer). The heads-up email fires once on this supervisor sign.
      const res = await evaluationsAPI.sign(id, { role: 'supervisor', signature: supSig, signature_comments: supSigComments });
      setShowSign(false); setCombinedSubmit(false);
      setEv(res.data);
      toast.success('Signed and submitted for review.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign and submit');
    }
  };


  const handleRegenerateLink = async () => {
    setRegenerating(true);
    try {
      await evaluationsAPI.regenerateEmployeeLink(id);
      toast.success('A new link has been emailed to the employee.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not regenerate link');
    } finally {
      setRegenerating(false);
    }
  };

  const handleAdminDelete = async () => {
    // Two-step confirmation because there is NO undo — the evaluation and any
    // associated PDF are removed from the record permanently. Admin-only.
    const empName = `${(ev?.employee_snapshot?.first_name) || ''} ${(ev?.employee_snapshot?.last_name) || ''}`.trim() || 'this employee';
    const first = window.confirm(
      `Admin override: permanently delete this ${ev?.evaluation_type || 'evaluation'} for ${empName}?\n\n` +
      `Status: ${ev?.status || 'unknown'}\n` +
      `Period: ${ev?.period_from} → ${ev?.period_to}\n\n` +
      `This cannot be undone. A full audit log entry will be recorded.`
    );
    if (!first) return;
    const typed = window.prompt('Type DELETE (all caps) to confirm.');
    if (typed !== 'DELETE') { toast.error('Confirmation text did not match. Delete cancelled.'); return; }
    setDeleting(true);
    try {
      await evaluationsAPI.remove(id);
      toast.success('Evaluation deleted.');
      navigate('/evaluations');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
      setDeleting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineNote.trim()) { toast.error('Please add a note explaining the decline'); return; }
    try {
      await evaluationsAPI.decline(id, { role: 'decline', signature: '', note: declineNote });
      toast.success('Evaluation declined');
      setShowDecline(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Decline failed');
    }
  };

  const handlePdf = async () => {
    try {
      const res = await evaluationsAPI.pdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const snap = ev?.employee_snapshot || {};
      const who = `${snap.first_name || ''}-${snap.last_name || ''}`.replace(/(^-|-$)/g, '') || id.slice(0, 8);
      const typeLabel = ev?.evaluation_type ? `${ev.evaluation_type} Evaluation` : 'Evaluation';
      const fname = `${typeLabel} - ${who}.pdf`.replace(/\s+/g, '-').replace(/\//g, '-');
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF generation failed'); }
  };

  const handleReturn = async () => {
    if (!returnReason.trim()) { toast.error('A reason is required to return the evaluation.'); return; }
    setReturnBusy(true);
    try {
      await evaluationsAPI.returnToSupervisor(id, { role: 'reviewer', signature: '', note: returnReason });
      toast.success('Returned to the supervisor.');
      setShowReturn(false); setReturnReason('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Return failed');
    } finally { setReturnBusy(false); }
  };

  const handleResponse = async () => {
    if (!responseText.trim()) { toast.error('Please write your response.'); return; }
    setResponseBusy(true);
    try {
      await evaluationsAPI.addResponse(id, { role: responseRole, signature: '', note: responseText });
      toast.success('Response added. A new PDF version has been emailed to the employee.');
      setShowResponse(false); setResponseText('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add response');
    } finally { setResponseBusy(false); }
  };

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 skeleton-pulse" /><div className="h-96 skeleton-pulse rounded-lg" /></div>;
  if (!ev || !template) return null;

  // New signing order helpers (records created before 2026-06 default to 'old').
  const isNewOrder = (ev.signing_order || 'old') === 'new';
  const addenda = ev.addenda || [];
  const myUid = user?.id;
  const sigSup = ev.signatures?.supervisor || {};
  const sigRev = ev.signatures?.reviewer || {};
  const isAdminHr = hasRole('admin', 'hr');
  // Which response slot(s) this user can fill (one per signer, permanent).
  const supResponded = addenda.some(a => a.role === 'supervisor');
  const revResponded = addenda.some(a => a.role === 'reviewer');
  const canRespondSupervisor = isComplete && isNewOrder && !supResponded &&
    (isAdminHr || sigSup.signer_user_id === myUid);
  const canRespondReviewer = isComplete && isNewOrder && !revResponded && !ev.is_collapsed_review &&
    (isAdminHr || sigRev.signer_user_id === myUid);
  const canRespond = canRespondSupervisor || canRespondReviewer;
  // Last reviewer return (shown to the supervisor when the eval is back in Draft).
  const lastReturn = [...(ev.history || [])].reverse().find(h => h.event === 'returned_by_reviewer');

  return (
    <div className="space-y-6 pb-24" data-testid="evaluation-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/evaluations')}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="page-title">{snap.first_name} {snap.last_name}</h1>
            <p className="text-slate-500 mt-1 text-sm">
              {snap.position} · {snap.post_type || 'Unarmed'} · Period {ev.period_from} → {ev.period_to}
            </p>
            <p className="text-slate-600 mt-1 text-xs font-semibold uppercase tracking-wide">
              {(() => {
                const t = ev.evaluation_type || 'Annual';
                if (t === '90-Day Trial') return '90-Day Trial Performance Evaluation';
                if (t === '6-Month') return '6-Month Performance Evaluation';
                return 'Annual Security Officer Performance Evaluation';
              })()}
            </p>
          </div>
          <Badge className="ml-3">{STATUS_LABELS[ev.status]}</Badge>
          <Badge
            variant="outline"
            className={`ml-2 text-[10px] ${isNewOrder ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-500'}`}
            title={isNewOrder
              ? 'New order: Supervisor → Reviewer → Employee (employee signs last).'
              : 'Legacy order: Employee → Supervisor → Reviewer. In-progress evaluations finish under the order they started.'}
            data-testid="eval-order-badge"
          >
            {isNewOrder ? 'New order' : 'Legacy order'}
          </Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isDraft && canWriteEvals && (
            <>
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-draft-btn">
                <Save className="w-4 h-4 mr-2" /> Save Draft
              </Button>
            </>
          )}
          {ev.status === 'awaiting_employee' && canWriteEvals && (
            <Button onClick={handleRegenerateLink} disabled={regenerating} variant="outline" data-testid="regenerate-link-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> {regenerating ? 'Sending...' : 'Resend / Regenerate Link'}
            </Button>
          )}
          {(ev.status === 'awaiting_supervisor') && canWriteEvals && (
            <Button onClick={() => openSignDialog('supervisor')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sign-supervisor-btn">
              <PenLine className="w-4 h-4 mr-2" />
              {ev.is_collapsed_review ? 'Supervisor + Reviewer Sign' : 'Supervisor Sign'}
            </Button>
          )}
          {ev.status === 'awaiting_reviewer' && canWriteEvals && (
            <Button onClick={() => openSignDialog('reviewer')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sign-reviewer-btn">
              <PenLine className="w-4 h-4 mr-2" /> Reviewer Sign
            </Button>
          )}
          {ev.status === 'awaiting_reviewer' && canWriteEvals && isNewOrder && (
            <Button variant="outline" onClick={() => setShowReturn(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    data-testid="return-to-supervisor-btn">
              <Undo2 className="w-4 h-4 mr-2" /> Return to Supervisor
            </Button>
          )}
          {canRespond && (
            <Button variant="outline" onClick={() => {
                      setResponseRole(canRespondSupervisor ? 'supervisor' : 'reviewer');
                      setShowResponse(true);
                    }}
                    className="border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
                    data-testid="add-response-btn">
              <MessageSquarePlus className="w-4 h-4 mr-2" /> Add Response
            </Button>
          )}
          {!isDraft && !isComplete && canWriteEvals && (
            <Button variant="outline" onClick={() => setShowDecline(true)} data-testid="decline-btn">
              <AlertCircle className="w-4 h-4 mr-2" /> Decline
            </Button>
          )}
          <Button variant="outline" onClick={handlePdf} data-testid="download-pdf-btn">
            <Printer className="w-4 h-4 mr-2" /> PDF
          </Button>
          {/* Admin-only hard delete override — works at any stage. Uses a two-step
              confirm because there's no undo (evaluations are stored server-side). */}
          {hasRole('admin') && (
            <Button
              variant="outline"
              onClick={handleAdminDelete}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="admin-delete-eval-btn"
              disabled={deleting}
            >
              <Trash2 className="w-4 h-4 mr-2" /> {deleting ? 'Deleting…' : 'Delete (Admin)'}
            </Button>
          )}
        </div>
      </div>

      {/* Reviewer return reason — shown to the supervisor when the evaluation
          has been sent back to Draft for changes. */}
      {isDraft && lastReturn && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="returned-reason-banner">
          <div className="flex items-start gap-2">
            <Undo2 className="w-4 h-4 text-amber-700 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-800">Returned by the reviewer{lastReturn.by_name ? ` (${lastReturn.by_name})` : ''}</p>
              <p className="text-amber-900 mt-1 whitespace-pre-wrap">{lastReturn.reason}</p>
              <p className="text-amber-700 text-xs mt-1">Make the requested changes, then submit again to re-route for signing.</p>
            </div>
          </div>
        </div>
      )}

      {/* Dated response addenda (new order) — never change the signed evaluation. */}
      {addenda.length > 0 && (
        <Card data-testid="response-addenda-card">
          <CardHeader><CardTitle className="flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Response Addenda</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Dated responses added after the evaluation was signed. They do not change the signed evaluation.</p>
            {addenda.map((a, i) => (
              <div key={a.id || i} className="rounded-md border border-slate-200 p-3" data-testid={`addendum-${i}`}>
                <p className="text-sm font-medium text-slate-700">{a.signer_name || '—'} <span className="text-slate-400">({(a.role || '').replace(/^\w/, c => c.toUpperCase())} · {(a.at || '').slice(0,10)})</span></p>
                <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{a.text}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Who can sign this? (v4.37.0) — visible to supervisory team, HR, admins */}
      {whoCanSign && (
        <Card data-testid="who-can-sign-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {whoCanSign.reviewer_needed
                ? <AlertCircle className="w-4 h-4 text-amber-600" />
                : <CheckCircle className="w-4 h-4 text-emerald-600" />}
              Who can sign this?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {whoCanSign.signed_reviewer && (
              <p className="text-sm text-emerald-700" data-testid="who-can-sign-signed">Reviewer signed by {whoCanSign.signed_reviewer}.</p>
            )}
            {whoCanSign.reviewer_needed ? (
              <p className="text-sm text-amber-700" data-testid="who-can-sign-needed">Reviewer needed, HR notified.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="who-can-sign-eligible">
                {whoCanSign.eligible_reviewers.map((r) => (
                  <Badge key={r.id} variant="outline" data-testid={`who-can-sign-${r.id}`}>
                    {r.name}{r.level_label ? ` · ${r.level_label}` : ''}
                  </Badge>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400">The reviewer line is signed by an Assistant PM or PM on this employee’s client supervisory team.</p>
          </CardContent>
        </Card>
      )}

      {/* Employee snapshot */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            Employee Information
            {isDraft && (
              <Badge variant="outline" className="text-[10px] gap-1" title="These fields are pulled live from the employee record while in Draft. They will be frozen at submission.">
                <RefreshCw className="w-3 h-3" />
                Live
              </Badge>
            )}
          </CardTitle>
          {isDraft && (
            <Button variant="ghost" size="sm" onClick={refreshLiveSnap} data-testid="refresh-live-snap-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-slate-500">Employee ID</p><p className="font-medium">{snap.employee_number || '—'}</p></div>
            <div><p className="text-slate-500">Site / Account</p><p className="font-medium">{snap.assignment_location || '—'}</p></div>
            <div><p className="text-slate-500">State</p><p className="font-medium">{stateLabel(snap.state) || snap.region || '—'}</p></div>
            <div><p className="text-slate-500">Direct Supervisor</p><p className="font-medium">{snap.direct_supervisor_name || '—'}</p></div>
            <div><p className="text-slate-500">Reviewer / Manager</p><p className="font-medium">{snap.reviewer_name || (ev.is_collapsed_review ? `${snap.direct_supervisor_name || '—'} (same)` : '—')}</p></div>
            <div><p className="text-slate-500">Hire Date</p><p className="font-medium">{snap.hire_date || '—'}</p></div>
            <div><p className="text-slate-500">Last Evaluation</p><p className="font-medium">{snap.last_evaluation_date ? formatDate(snap.last_evaluation_date) : '—'}</p></div>
            <div>
              <p className="text-slate-500">Employee Email</p>
              <p className={cn('font-medium truncate', !snap.email && 'text-red-600')} title={snap.email || ''}>{snap.email || 'Not on file'}</p>
            </div>
          </div>
          {isDraft && !snap.email && (
            <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm flex items-start gap-2" data-testid="missing-email-warning">
              <AlertCircle className="w-4 h-4 mt-0.5 text-red-700 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-900">Employee email is required before submitting</p>
                <p className="text-red-800">Open the <Link to={`/employees/${ev.employee_id}`} className="underline font-medium">employee record</Link> in a new tab, add an email address, save, then click <b>Refresh</b> above. The Submit for Signing button will enable as soon as the email is detected.</p>
              </div>
            </div>
          )}
          {ev.status === 'awaiting_employee' && (
            <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm flex items-start gap-2" data-testid="awaiting-employee-banner">
              <Mail className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Waiting on employee signature</p>
                <p className="text-blue-800">A secure link was emailed to <b>{snap.email || '—'}</b>{ev.employee_token_expires_at ? <> and expires on <b>{formatDate(ev.employee_token_expires_at)}</b></> : null}. Use the Resend / Regenerate Link button to email a fresh one.</p>
                {ev.employee_token && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <code className="text-xs bg-surface border border-blue-200 px-2 py-1 rounded select-all break-all" data-testid="employee-sign-link">
                      {`${window.location.origin}/public/evaluations/${ev.employee_token}`}
                    </code>
                    <button
                      type="button"
                      onClick={async () => {
                        const url = `${window.location.origin}/public/evaluations/${ev.employee_token}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success('Sign-in link copied to clipboard');
                        } catch (_e) {
                          toast.error('Could not copy — long-press the link to copy manually');
                        }
                      }}
                      className="text-xs px-2 py-1 rounded bg-blue-700 hover:bg-blue-800 text-blue-50"
                      data-testid="copy-employee-link-btn"
                    >
                      Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Armed Officer Compliance — applies to legacy "Armed" + new finer-grained labels */}
      {['Armed','Armed Security Guard','Special Police Officer','Peace Officer','Shift Supervisor'].includes(snap.post_type) && (
        <Card>
          <CardHeader><CardTitle>Armed Officer Compliance</CardTitle><CardDescription>Snapshot of the employee&apos;s primary license at evaluation creation. Edits must be made in the Licensing section of the employee record.</CardDescription></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Licensed State</Label><Input value={licensedState} readOnly className="font-mono bg-slate-50" data-testid="licensed-state" /></div>
            <div><Label>State Security License #</Label><Input value={armedLicNum} readOnly className="font-mono bg-slate-50" data-testid="armed-lic-num" /></div>
            <div><Label>License Expiration</Label><Input type="date" value={armedLicExp} readOnly className="bg-slate-50" data-testid="armed-lic-exp" /></div>
            {!licensedState && !armedLicNum && !armedLicExp && (
              <p className="text-sm text-slate-500 md:col-span-3 italic">No active licenses on file.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 2: 1–5 Rating Scale reference (mobile-collapsible, verbatim
          copy from Paul Christian's Aug 2026 doc, editable via /admin/evaluation-settings) */}
      <RatingScalePanel intro={template.rating_scale_intro} levels={template.rating_scale_levels} />

      {/* Sections */}
      {template.sections.map(sec => {
        const incompleteCount = (sec.items || []).filter(it => {
          const r = ratings[it.id] || {};
          return !((typeof r.rating === 'number' && r.rating >= 1) || r.na);
        }).length;
        const isIncomplete = isDraft && incompleteCount > 0;
        return (
        <Card key={sec.id} data-eval-incomplete={isIncomplete ? 'true' : 'false'} className={cn(isIncomplete && 'ring-2 ring-amber-300')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Section {sec.id}: {sec.title}
              {isIncomplete && <Badge className="bg-amber-100 text-amber-800" data-testid={`section-incomplete-${sec.id}`}>{incompleteCount} unrated</Badge>}
            </CardTitle>
            <CardDescription>
              {sectionAvgs[sec.id] != null ? <>Section Avg: <span className="font-mono font-semibold">{sectionAvgs[sec.id].toFixed(2)}</span></> : 'No items rated yet'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sec.items.map(item => {
              const r = ratings[item.id] || {};
              const itemIncomplete = isDraft && !((typeof r.rating === 'number' && r.rating >= 1) || r.na);
              return (
                <div key={item.id} className={cn('border rounded-md p-3', itemIncomplete && 'border-amber-400 bg-amber-50/40')} data-testid={`item-${item.id}`}>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    {item.text}
                    {itemIncomplete && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`item-unrated-${item.id}`}>Unrated</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[5,4,3,2,1].map(v => (
                      <button
                        key={v}
                        type="button"
                        disabled={!isDraft}
                        onClick={() => updateRating(item.id, { rating: v, na: false })}
                        data-testid={`rate-${item.id}-${v}`}
                        className={cn(
                          'min-w-[2.5rem] h-10 px-3 rounded-md text-sm font-bold border transition-colors',
                          r.rating === v && !r.na ? 'bg-brand text-brand-contrast border-brand' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100',
                          !isDraft && 'opacity-60 cursor-not-allowed'
                        )}
                      >{v}</button>
                    ))}
                    <button
                      type="button"
                      disabled={!isDraft}
                      onClick={() => updateRating(item.id, { rating: null, na: true })}
                      data-testid={`rate-${item.id}-na`}
                      className={cn(
                        'min-w-[3rem] h-10 px-3 rounded-md text-sm font-bold border transition-colors',
                        r.na ? 'bg-amber-500 text-amber-50 border-amber-500' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100',
                        !isDraft && 'opacity-60 cursor-not-allowed'
                      )}
                    >N/A</button>
                  </div>
                  <Textarea
                    placeholder={(() => {
                      const req = new Set(template?.comment_required_ratings || [1, 2]);
                      if (r.rating != null && !r.na && req.has(r.rating)) {
                        return `Comments required (rated ${r.rating})`;
                      }
                      return 'Comments (optional)';
                    })()}
                    rows={2}
                    value={r.comments || ''}
                    onChange={(e) => updateRating(item.id, { comments: e.target.value })}
                    onPaste={(e) => {
                      const raw = e.clipboardData?.getData('text');
                      if (raw == null) return;
                      e.preventDefault();
                      updateRating(item.id, { comments: (r.comments || '') + stripMarkdown(raw) });
                    }}
                    disabled={!isDraft}
                    data-testid={`comments-${item.id}`}
                    className={(() => {
                      const req = new Set(template?.comment_required_ratings || [1, 2]);
                      const needs = r.rating != null && !r.na && req.has(r.rating) && !(r.comments || '').trim();
                      return needs ? 'border-red-400 focus:border-red-500' : '';
                    })()}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
        );
      })}

      {/* Narratives. 2026-08-23: 90-Day evaluations no longer capture the
          Strengths / Areas / Goals / Training narrative fields — the
          Recommendation card's required Justification is the sole write-up.
          Annual keeps the full set. */}
      <Card>
        <CardHeader>
          <CardTitle>
            {ev.evaluation_type === '90-Day Trial' ? 'Comments' : 'Summary & Goals'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {ev.evaluation_type !== '90-Day Trial' && (
            <>
              <div><Label>Summary of Strengths</Label><Textarea rows={3} value={strengths} onChange={(e) => setStrengths(e.target.value)} onPaste={handlePlainPaste(setStrengths)} disabled={!isDraft} data-testid="strengths" /></div>
              <div><Label>Areas for Development</Label><Textarea rows={3} value={areas} onChange={(e) => setAreas(e.target.value)} onPaste={handlePlainPaste(setAreas)} disabled={!isDraft} data-testid="areas" /></div>
              <div><Label>Goals for Next Review Period (2–3 specific, measurable)</Label><Textarea rows={3} value={goals} onChange={(e) => setGoals(e.target.value)} onPaste={handlePlainPaste(setGoals)} disabled={!isDraft} data-testid="goals" /></div>
              <div><Label>Training &amp; Resources Assigned</Label><Textarea rows={2} value={training} onChange={(e) => setTraining(e.target.value)} onPaste={handlePlainPaste(setTraining)} disabled={!isDraft} data-testid="training" /></div>
            </>
          )}
          <div><Label>Supervisor Comments</Label><Textarea rows={3} value={supComments} onChange={(e) => setSupComments(e.target.value)} onPaste={handlePlainPaste(setSupComments)} disabled={!isDraft} data-testid="sup-comments" /></div>
          <div>
            <Label>Employee Comments</Label>
            <Textarea
              rows={3}
              value={empComments}
              readOnly
              disabled
              data-testid="emp-comments"
              placeholder="The employee fills this in during their secure sign-in step. Read-only here."
            />
          </div>
          {ev.supervisor_signature_comments && (
            <div>
              <Label>Supervisor Signature Comments</Label>
              <Textarea rows={2} value={ev.supervisor_signature_comments} readOnly disabled data-testid="sup-sig-comments-display" />
            </div>
          )}
          {ev.reviewer_signature_comments && (
            <div>
              <Label>Reviewer / Manager Signature Comments</Label>
              <Textarea rows={2} value={ev.reviewer_signature_comments} readOnly disabled data-testid="rev-sig-comments-display" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* 90-Day Trial Recommendation (only for 90-Day Trial evals) */}
      {(ev.evaluation_type === '90-Day Trial') && (
        <Card data-testid="trial-recommendation-card" className="border-slate-300">
          <CardHeader>
            <CardTitle>90-Day Trial Recommendation</CardTitle>
            <CardDescription>Final decision on the officer&apos;s probationary period. Required before submitting.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label>Recommendation *</Label>
              <div className="flex flex-wrap gap-2" data-testid="rec-options">
                {['Retain', 'Extend', 'Separate'].map(v => (
                  <button
                    type="button"
                    key={v}
                    disabled={!isDraft}
                    onClick={() => setRecommendation(v)}
                    data-testid={`rec-${v.toLowerCase()}`}
                    className={cn(
                      'px-4 h-10 rounded-md text-sm font-semibold border transition-colors',
                      recommendation === v ? 'bg-brand text-brand-contrast border-brand' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100',
                      !isDraft && 'opacity-60 cursor-not-allowed'
                    )}
                  >{v}</button>
                ))}
              </div>
            </div>
            {recommendation && (
              <div className="space-y-1">
                <Label>Justification *</Label>
                <Textarea
                  rows={4}
                  value={recommendationBasis}
                  onChange={(e) => setRecommendationBasis(e.target.value)}
                  onPaste={handlePlainPaste(setRecommendationBasis)}
                  disabled={!isDraft}
                  placeholder="Explain why you chose this recommendation — specific behaviors, incidents, or performance data. Required for every recommendation."
                  data-testid="rec-basis"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supervisor Log — user request 2026-09-08: the same log from the
          employee record, read-only here so a manager can see the entries
          while writing the review. Gated on employees:view rather than a role
          list so supervisors and GSOC supervisors see it too; the API is
          span-of-control gated and the panel goes quiet on a 403. */}
      {ev.employee_id && perms.can('employees', 'view') && (
        <SupervisorLogPanel employee={{ id: ev.employee_id, first_name: snap.first_name, last_name: snap.last_name }} readOnly />
      )}

      {ev.credential_headsup_no_email && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm flex items-start gap-2" data-testid="credential-headsup-no-email-banner">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <p className="text-amber-900"><b>No email on file, heads-up not sent.</b> The employee has no email address, so the credentials heads-up email could not be sent. Add an email on the employee record if you want them notified.</p>
        </div>
      )}
      <CredentialsPanel evaluationId={id} />

      {/* Bottom Submit-for-Signing card (primary action) */}
      {isDraft && canWriteEvals && (() => {
        const missingEmail = !((snap.email || '').trim());
        const canSubmit = submissionReadiness.ok && !missingEmail;
        return (
        <Card data-testid="bottom-submit-card" className={cn(!canSubmit && 'border-amber-300 bg-amber-50')}>
          <CardContent className="py-6 space-y-3">
            {missingEmail && (
              <div className="text-sm text-red-900 flex items-start gap-2" data-testid="bottom-missing-email">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Employee email missing</p>
                  <p>Add an email address to the <Link to={`/employees/${ev.employee_id}`} className="underline font-medium">employee record</Link>, then click <b>Refresh</b> in the Employee Information card.</p>
                </div>
              </div>
            )}
            {!submissionReadiness.ok ? (
              <div className="text-sm text-amber-900">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertCircle className="w-4 h-4" />
                  Cannot submit yet — every item must be rated or marked N/A:
                </div>
                <ul className="list-disc ml-7" data-testid="submission-blockers">
                  {submissionReadiness.sectionsWithMisses.map((m, idx) => (
                    <li key={idx}><b>{m.section}</b> — {m.missing.length} unrated</li>
                  ))}
                </ul>
              </div>
            ) : !missingEmail ? (
              <p className="text-sm text-emerald-800 flex items-center gap-2"><CheckCircle className="w-4 h-4" />All items rated. {isNewOrder ? 'Ready to sign and submit for review.' : 'Ready to submit for signing.'}</p>
            ) : null}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="bottom-save-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                size="lg"
                onClick={isNewOrder ? startSupervisorSignSubmit : handleSubmit}
                disabled={saving || !canSubmit}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="bottom-submit-eval-btn"
              >
                <Send className="w-4 h-4 mr-2" /> {isNewOrder ? 'Sign & Submit for Review' : 'Submit for Signing'}
              </Button>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Sticky overall summary bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-surface border-t shadow-lg px-4 py-3 lg:left-64">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-slate-500">Overall Score:</span>{' '}
              <span className="font-mono font-bold text-lg">{overall != null ? overall.toFixed(2) : '—'}</span>
            </div>
            <div>
              <span className="text-slate-500">Rating:</span>{' '}
              <span className="font-semibold">{ev.overall_band || overallBand || '—'}</span>
            </div>
            {/* Show override reason + HR-review warning surfaced by the backend. */}
            {ev.override_reason && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 max-w-md" data-testid="override-reason">
                <strong>Capped:</strong> {ev.override_reason}
              </div>
            )}
            {ev.hr_review_required && (
              <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="hr-review-flag">
                <strong>HR review required:</strong> item(s) {(ev.hr_review_items || []).join(', ')} at/below minimum
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            {(['employee','supervisor', ...(ev.is_collapsed_review ? [] : ['reviewer'])]).map(role => {
              const s = (ev.signatures || {})[role];
              return (
                <span key={role} className="flex items-center gap-1">
                  {s ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <span className="w-3.5 h-3.5 rounded-full bg-slate-200" />}
                  <span className="capitalize">{role}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* 2026-08-22: 6-Month Re-Review (Part 2). Only rendered for 90-Day Trial
          evaluations that have been fully signed & completed. Part 1 stays
          locked read-only above; Part 2 lives on the SAME evaluation record. */}
      {ev.evaluation_type === '90-Day Trial' && ev.status === 'complete' && (
        <Part2Panel ev={ev} onChanged={() => loadAll({ silent: true })} />
      )}

      {/* Supervisor Log — user request 2026-09-08: "this is the same log that
          should appear at the bottom of evaluation form so managers can quickly
          see log entries". Read-only here; entries are added on the employee
          record. Renders nothing for a caller outside the span (the API 403s
          and the panel handles that silently). */}

      {/* Sign dialog */}
      <Dialog open={showSign} onOpenChange={setShowSign}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {signRole === 'supervisor' && (ev.is_collapsed_review ? 'Supervisor + Reviewer Signature' : 'Direct Supervisor Signature')}
              {signRole === 'reviewer' && 'Reviewer / Manager Signature'}
            </DialogTitle>
            <DialogDescription>
              Add a comment (optional) and capture your signature below. {combinedSubmit ? 'Confirming will submit this evaluation to the reviewer (or the next step when there is no reviewer). ' : ''}{ev.is_collapsed_review && signRole === 'supervisor' ? (isNewOrder ? 'Because the supervisor and reviewer are the same person, this single signature covers both internal steps; the employee still signs last.' : 'Because the supervisor and reviewer are the same person, this single signature completes both signing steps.') : ''}
            </DialogDescription>
          </DialogHeader>
          {signRole === 'supervisor' && (
            <div className="space-y-4">
              <div>
                <Label>{ev.is_collapsed_review ? 'Supervisor / Reviewer Comments (optional)' : 'Supervisor Signature Comments (optional)'}</Label>
                <Textarea rows={3} value={supSigComments} onChange={(e) => setSupSigComments(e.target.value)} placeholder="These comments are visible to subsequent signers and appear on the final PDF." data-testid="sup-sig-comments" />
              </div>
              <SignaturePad label="Direct Supervisor Signature" onSignatureChange={setSupSig} testId="sup-sig" />
            </div>
          )}
          {signRole === 'reviewer' && (
            <div className="space-y-4">
              <div>
                <Label>Reviewer / Manager Signature Comments (optional)</Label>
                <Textarea rows={3} value={revSigComments} onChange={(e) => setRevSigComments(e.target.value)} placeholder="Final comments before completion. Appears on the signed PDF." data-testid="rev-sig-comments" />
              </div>
              <SignaturePad label="Reviewer / Manager Signature" onSignatureChange={setRevSig} testId="rev-sig" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowSign(false); setCombinedSubmit(false); }}>Cancel</Button>
            <Button onClick={combinedSubmit ? handleSignAndSubmit : handleSign} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-sign-btn">{combinedSubmit ? 'Confirm, Sign & Submit' : 'Confirm & Sign'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Evaluation</DialogTitle>
            <DialogDescription>Provide a reason. This will halt the signing workflow.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={declineNote} onChange={(e) => setDeclineNote(e.target.value)} placeholder="Reason for decline..." data-testid="decline-note" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDecline(false)}>Cancel</Button>
            <Button onClick={handleDecline} variant="destructive" data-testid="confirm-decline-btn">Decline</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return to supervisor (reviewer, new order) */}
      <Dialog open={showReturn} onOpenChange={setShowReturn}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Supervisor</DialogTitle>
            <DialogDescription>
              The evaluation goes back to Draft for the supervisor and their signature is cleared.
              You cannot change the ratings — explain what needs to change. A reason is required.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={returnReason} onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="What should the supervisor revise?" data-testid="return-reason" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowReturn(false)}>Cancel</Button>
            <Button onClick={handleReturn} disabled={returnBusy}
                    className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-return-btn">
              {returnBusy ? 'Returning…' : 'Return to Supervisor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Optional response addendum (supervisor/reviewer, after employee signs) */}
      <Dialog open={showResponse} onOpenChange={setShowResponse}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a Response</DialogTitle>
            <DialogDescription>
              A response is a dated addendum — it never changes the signed evaluation. You may add one,
              and it cannot be edited or removed afterward. A new PDF version is emailed to the employee.
            </DialogDescription>
          </DialogHeader>
          {canRespondSupervisor && canRespondReviewer && (
            <div className="flex gap-2 text-sm" data-testid="response-role-picker">
              <Button variant={responseRole === 'supervisor' ? 'default' : 'outline'} size="sm"
                      onClick={() => setResponseRole('supervisor')}>As Supervisor</Button>
              <Button variant={responseRole === 'reviewer' ? 'default' : 'outline'} size="sm"
                      onClick={() => setResponseRole('reviewer')}>As Reviewer</Button>
            </div>
          )}
          <Textarea rows={4} value={responseText} onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Your response…" data-testid="response-text" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowResponse(false)}>Cancel</Button>
            <Button onClick={handleResponse} disabled={responseBusy}
                    className="bg-sky-600 hover:bg-sky-700" data-testid="confirm-response-btn">
              {responseBusy ? 'Saving…' : 'Add Response'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
